import LayoutStyles from '@Styles/layout.scss';
import { Modal } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import OrgInfoUpdateScreen from '@Components/Settings/views/OrgAdmin/OrgInfoUpdateScreen/OrgInfoUpdateScreen';
import OrgMembersTable from '@Components/Settings/views/OrgAdmin/OrgMembersTable/OrgMembersTable';
import OrgTeamsTable from '@Components/Settings/views/OrgAdmin/OrgTeamsTable/OrgTeamsTable';
import OrganizationSettingsTable from '@Components/Settings/views/SuperAdmin/OrganizationSettingsTable/OrganizationSettingsTable';
import SuperAdminSettingsTable from '@Components/Settings/views/SuperAdmin/SuperAdminSettingsTable/SuperAdminSettingsTable';
import TeamInfoUpdateScreen from '@Components/Settings/views/TeamAdmin/TeamInfoUpdateScreen/TeamInfoUpdateScreen';
import TeamMembersTable from '@Components/Settings/views/TeamAdmin/TeamMembersTable/TeamMembersTable';
import { selectedOrganizationState } from '@Data/organizations/Reducer';
import { selectedProjectState } from '@Data/projects/Reducer';
import { selectedTeamState } from '@Data/teams/Reducer';
import { ISettingsOption } from './Interfaces';
import { SettingsType } from './SettingsType';
import SettingsDialogInnerComponent from './components/SettingsDialogInnerComponent/SettingsDialogInnerComponent';
import { ProjectAutoQcConfig } from './views/Projects/ProjectAutoQcConfig/ProjectAutoQcConfig';
import { ProjectDocumentsTable } from './views/Projects/ProjectDocumentsTable/ProjectDocumentsTable';

interface ISettingsDialogueProps {
    isOpen: boolean;
    onClose: () => void;
    type: SettingsType;
    isSubmitting: boolean;
    setIsSubmitting: React.Dispatch<React.SetStateAction<boolean>>;
}

const SettingsDialogue = (props: ISettingsDialogueProps) => {
    const { isOpen, onClose, type, isSubmitting, setIsSubmitting } = props;

    const [userTitle, setUserTitle] = useState<string>('');
    const [editEntity, setEditEntity] = useState<string | null>(null);
    const selectedOrg = useSelector(selectedOrganizationState);
    const selectedTeam = useSelector(selectedTeamState);
    const [settingsOptions, setSettingsOptions] = useState<
        Array<ISettingsOption>
    >([]);
    const selectedProject = useSelector(selectedProjectState);

    useEffect(() => {
        switch (type) {
            case SettingsType.SUPERADMIN:
                setUserTitle('Super Admin');
                setEditEntity(null);
                setSettingsOptions([
                    {
                        baseComponent: (
                            <OrganizationSettingsTable
                                isSubmitting={isSubmitting}
                                setIsSubmitting={setIsSubmitting}
                            />
                        ),
                        title: 'Organization Management',
                        id: 'organization-management',
                    },
                    {
                        baseComponent: (
                            <SuperAdminSettingsTable
                                isSubmitting={isSubmitting}
                                setIsSubmitting={setIsSubmitting}
                            />
                        ),
                        title: 'Super Admin Management',
                        id: 'superadmin-management',
                    },
                ]);
                break;
            case SettingsType.ORGADMIN:
                setUserTitle('Organization Admin');
                setEditEntity(selectedOrg?.organisation_name);
                setSettingsOptions([
                    {
                        baseComponent: (
                            <OrgInfoUpdateScreen
                                isSubmitting={isSubmitting}
                                setIsSubmitting={setIsSubmitting}
                            />
                        ),
                        title: 'Organization Details',
                        id: 'organization-details',
                    },
                    {
                        baseComponent: (
                            <OrgMembersTable
                                isSubmitting={isSubmitting}
                                setIsSubmitting={setIsSubmitting}
                            />
                        ),
                        title: 'Members Management',
                        id: 'organization-members-management',
                    },
                    {
                        baseComponent: (
                            <OrgTeamsTable
                                isSubmitting={isSubmitting}
                                setIsSubmitting={setIsSubmitting}
                            />
                        ),
                        title: 'Teams Management',
                        id: 'teams-management',
                    },
                    // { baseComponent: <OrgAdminsTable isSubmitting={isSubmitting} setIsSubmitting={setIsSubmitting} />, title: 'Org Admin Management' },
                ]);
                break;
            case SettingsType.TEAMSADMIN:
                setUserTitle('Team Admin');
                setEditEntity(selectedTeam?.team_name);
                setSettingsOptions([
                    {
                        baseComponent: (
                            <TeamInfoUpdateScreen
                                isSubmitting={isSubmitting}
                                setIsSubmitting={setIsSubmitting}
                            />
                        ),
                        title: 'Team Details',
                        id: 'team-details',
                    },
                    {
                        baseComponent: (
                            <TeamMembersTable
                                isSubmitting={isSubmitting}
                                setIsSubmitting={setIsSubmitting}
                            />
                        ),
                        title: 'Members Management',
                        id: 'team-members-management',
                    },
                ]);
                break;
            case SettingsType.PROJECT:
                setUserTitle('Project Settings');
                setEditEntity(selectedProject?.project_name);
                setSettingsOptions([
                    {
                        baseComponent: <ProjectDocumentsTable />,
                        title: 'Documents',
                        id: 'project-documents',
                    },
                    {
                        baseComponent: <ProjectAutoQcConfig />,
                        title: 'Auto QC Config',
                        id: 'project-auto-qc-config',
                    },
                ]);
                break;
        }
    }, [type, selectedOrg, selectedTeam, selectedProject]);

    return (
        <Modal
            id="settings-dialogue"
            open={isOpen}
            onClose={onClose}
            hideBackdrop={true}
            sx={{
                display: 'flex',
                backgroundColor: 'rgba(255, 255, 255, 1)',
                top: LayoutStyles.headerHeight,
                zIndex: 500,
            }}
        >
            <React.Fragment>
                <SettingsDialogInnerComponent
                    key={userTitle}
                    title={userTitle}
                    editEntity={editEntity}
                    onClose={onClose}
                    settingsOptions={settingsOptions}
                    isSubmitting={isSubmitting}
                />
            </React.Fragment>
        </Modal>
    );
};

export default SettingsDialogue;
